import { useEffect, useState, type ReactNode } from "react";
import { motion, useReducedMotion } from "motion/react";

export { useReducedMotion };

/**
 * Mirror of `useReducedMotion` but usable in plain effects where we want to
 * make a one-off decision (e.g. whether to run the power-on intro at all).
 */
export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return reduced;
}

interface RevealProps {
  children: ReactNode;
  /** Stagger order — multiplies the delay. */
  delay?: number;
  /** Extra px to travel up from. Defaults to 24. */
  y?: number;
  className?: string;
  as?: "div" | "section" | "li" | "h2" | "p";
}

/**
 * Scroll-triggered reveal: fades + slides up the first time it enters the
 * viewport. Honors prefers-reduced-motion by rendering content already-visible.
 */
export function Reveal({ children, delay = 0, y = 24, className, as = "div" }: RevealProps) {
  const reduced = useReducedMotion();
  const MotionTag = motion[as] as typeof motion.div;

  if (reduced) {
    const Tag = as as "div";
    return <Tag className={className}>{children}</Tag>;
  }

  return (
    <MotionTag
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </MotionTag>
  );
}
